import React from 'react';
import { useTranslation } from 'react-i18next';

// hooks
import { usePosthogCapture } from 'eCoach/posthogCapture/chat';

// components
import ChatLanding from 'eCoach/chatStart/ChatLanding';
import PromptHelper from 'eCoach/promptHelper/_index';

// utils
import { UserInputType } from 'eCoach/utils';

interface ChatGuideData {
  displayPolicyHelper?: boolean;
  displayPolicyPrompt?: boolean;
  displayUnionHelper?: boolean;
  displayUnionPrompt?: boolean;
  displayPromptHelper: boolean;
  chatStartEnded: boolean;
  inProgress: boolean;
  promptHelperGoal: string | null | any;
  promptHelperTheme: string | null;
}

interface ChatStartProps {
  chatGuideData: ChatGuideData;
  handleChatGuidance: (updates: Partial<ChatGuideData>) => void;
  sendUserInput: (text: string, type: number) => void;
}

export default function ChatStart({
  chatGuideData,
  handleChatGuidance,
  sendUserInput,
}: ChatStartProps): JSX.Element {
  const { t } = useTranslation('pageEcoach');
  const chat = usePosthogCapture();

  const openPromptHelper = (): void => {
    const updates: Partial<ChatGuideData> = {
      displayPromptHelper: true,
      chatStartEnded: false,
      inProgress: true,
    };

    handleChatGuidance(updates);
    chat.messageInitialPromptSelectedPromptHelper();
  };

  const activatePolicyHelper = (): void => {
    const updates: Partial<ChatGuideData> = {
      displayPolicyHelper: true,
      displayPolicyPrompt: true,
      chatStartEnded: false,
      inProgress: true,
    };

    handleChatGuidance(updates);
    chat.messageInitialPromptSelectedPolicyHelper();
  };

  const activateUnionHelper = (): void => {
    const updates: Partial<ChatGuideData> = {
      displayUnionHelper: true,
      displayUnionPrompt: true,
      chatStartEnded: false,
      inProgress: true,
    };

    handleChatGuidance(updates);
    chat.messageInitialPromptSelectedUnionRules();
  };

  const startFormFilling = (): void => {
    sendUserInput(
      t('chatStart.I want to write a warning letter.'),
      UserInputType.FORM_INSTANCE_START
    );
  };

  const startConvoPrep = (): void => {
    sendUserInput(
      t('chatStart.I need help preparing for a conversation with someone.'),
      UserInputType.CONVERSATION_PREP_START
    );
    chat.messageInitialPromptSelectedConvoPrep();
  };

  const onChallengeSubmission = (userInput: string): void => {
    if (
      chatGuideData.promptHelperTheme ===
      'Preparing for a difficult conversation'
    ) {
      sendUserInput(userInput, UserInputType.CONVERSATION_PREP_WORRIED_BY);
    } else {
      sendUserInput(userInput, UserInputType.INITIAL_PROMPT);
    }

    const updates: Partial<ChatGuideData> = {
      inProgress: false,
      chatStartEnded: true,
      displayPolicyPrompt: false,
      displayUnionPrompt: false,
    };
    handleChatGuidance(updates);
    chat.messageInitialPromptSubmitted();
  };

  if (
    chatGuideData.displayPromptHelper ||
    chatGuideData.promptHelperTheme === 'Preparing for a difficult conversation'
  )
    return (
      <PromptHelper
        chatGuideData={chatGuideData}
        handleChatGuidance={handleChatGuidance}
        onSubmit={onChallengeSubmission}
        startConvoPrep={startConvoPrep}
      />
    );

  return (
    <ChatLanding
      activatePolicyHelper={activatePolicyHelper}
      activateUnionHelper={activateUnionHelper}
      handleChatGuidance={handleChatGuidance}
      onSubmit={onChallengeSubmission}
      openPromptHelper={openPromptHelper}
      startFormFilling={startFormFilling}
    />
  );
}
